import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import { 
    UserPlus, 
    Mail, 
    Copy, 
    Check,
    Trash2,
    RefreshCw,
    Link as LinkIcon,
    Loader2,
    Send,
    CheckCircle,
    XCircle,
    MailCheck,
    MailX
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminInvitationsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [resendingId, setResendingId] = useState(null);
    
    const [formData, setFormData] = useState({
        email: '',
        name: ''
    });

    useEffect(() => {
        fetchInvitations();
    }, []);

    const fetchInvitations = async () => {
        try {
            const response = await axios.get(`${API}/admin/invitations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvitations(response.data);
        } catch (error) {
            console.error('Failed to fetch invitations:', error);
            toast.error('Einladungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.email) {
            toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post(
                `${API}/admin/invitations`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Check if email was sent successfully
            if (response.data.email_sent) {
                toast.success(`Einladung an ${formData.email} wurde versendet`);
            } else {
                toast.warning(`Einladung erstellt, aber E-Mail konnte nicht gesendet werden. Link wurde kopiert.`);
                // Copy the link to clipboard as fallback
                const link = `${window.location.origin}/o/${tenant?.organization_code}/invite?token=${response.data.token}`;
                await navigator.clipboard.writeText(link);
            }
            
            setDialogOpen(false);
            setFormData({ email: '', name: '' });
            fetchInvitations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Einladung konnte nicht erstellt werden');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyLink = async (invitation) => {
        const link = `${window.location.origin}/o/${tenant?.organization_code}/invite?token=${invitation.token}`;
        await navigator.clipboard.writeText(link);
        setCopiedId(invitation.id);
        toast.success('Link kopiert');
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleResend = async (invitationId) => {
        setResendingId(invitationId);
        try {
            const response = await axios.post(
                `${API}/admin/invitations/${invitationId}/resend`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.email_sent) {
                toast.success('Einladung wurde erneut versendet');
            } else {
                toast.warning('E-Mail konnte nicht gesendet werden');
            }
            fetchInvitations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erneut senden fehlgeschlagen');
        } finally {
            setResendingId(null);
        }
    };

    const handleDelete = async (invitationId) => {
        try {
            await axios.delete(`${API}/admin/invitations/${invitationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Einladung gelöscht');
            fetchInvitations();
        } catch (error) {
            toast.error('Löschen fehlgeschlagen');
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Ausstehend</Badge>;
            case 'accepted':
                return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Angenommen</Badge>;
            case 'expired':
                return <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">Abgelaufen</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Einladungen</h1>
                        <p className="text-slate-600 mt-1">
                            Laden Sie Kunden zu Ihrem Incentive-Programm ein
                        </p>
                    </div>
                    <Button
                        style={{ backgroundColor: tenant?.primary_color }}
                        onClick={() => setDialogOpen(true)}
                        data-testid="create-invitation-btn"
                    >
                        <UserPlus size={18} className="mr-2" />
                        Kunde einladen
                    </Button>
                </div>

                {/* Invitations Table */}
                <Card data-testid="invitations-card">
                    <CardHeader>
                        <CardTitle>Alle Einladungen</CardTitle>
                        <CardDescription>
                            Verwalten Sie Ihre versendeten Einladungen
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : invitations.length === 0 ? (
                            <div className="text-center py-12">
                                <Mail size={48} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500 mb-4">Noch keine Einladungen versendet</p>
                                <Button
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    onClick={() => setDialogOpen(true)}
                                >
                                    <UserPlus size={18} className="mr-2" />
                                    Erste Einladung erstellen
                                </Button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>E-Mail</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Erstellt</TableHead>
                                            <TableHead>E-Mail gesendet</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Aktionen</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {invitations.map((invitation) => (
                                            <TableRow key={invitation.id} data-testid={`invitation-row-${invitation.id}`}>
                                                <TableCell>
                                                    <span className="font-medium text-slate-900">{invitation.email}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-slate-600">{invitation.name || '-'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-slate-600 text-sm">{formatDate(invitation.created_at)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {invitation.email_sent ? (
                                                        <div className="flex items-center gap-1.5 text-green-600">
                                                            <MailCheck size={16} />
                                                            <span className="text-sm">Ja</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 text-slate-400">
                                                            <MailX size={16} />
                                                            <span className="text-sm">Nein</span>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(invitation.status)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {invitation.status === 'pending' && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleCopyLink(invitation)}
                                                                    title="Link kopieren"
                                                                    data-testid={`copy-link-${invitation.id}`}
                                                                >
                                                                    {copiedId === invitation.id ? (
                                                                        <Check size={16} className="text-green-600" />
                                                                    ) : (
                                                                        <LinkIcon size={16} />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleResend(invitation.id)}
                                                                    disabled={resendingId === invitation.id}
                                                                    title="Erneut senden"
                                                                    data-testid={`resend-${invitation.id}`}
                                                                >
                                                                    {resendingId === invitation.id ? (
                                                                        <Loader2 size={16} className="animate-spin" />
                                                                    ) : (
                                                                        <RefreshCw size={16} />
                                                                    )}
                                                                </Button>
                                                            </>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => handleDelete(invitation.id)}
                                                            title="Löschen"
                                                            data-testid={`delete-${invitation.id}`}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Create Invitation Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Kunde einladen</DialogTitle>
                        <DialogDescription>
                            Senden Sie eine Einladung an einen neuen Kunden
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail-Adresse *</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="kunde@beispiel.de"
                                data-testid="invitation-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="name">Name (optional)</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Max Mustermann"
                                data-testid="invitation-name-input"
                            />
                        </div>
                        
                        <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
                            <p className="flex items-center gap-2">
                                <Send size={16} />
                                {tenant?.email_config?.from_email 
                                    ? 'Eine Einladungs-E-Mail wird automatisch versendet.'
                                    : 'Es wird ein Einladungslink erstellt, den Sie manuell versenden können.'
                                }
                            </p>
                        </div>
                        
                        <DialogFooter className="pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setDialogOpen(false)}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                type="submit"
                                style={{ backgroundColor: tenant?.primary_color }}
                                disabled={submitting}
                                data-testid="send-invitation-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Wird gesendet...
                                    </>
                                ) : (
                                    <>
                                        <Mail size={18} className="mr-2" />
                                        Einladung senden
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};
